/**
 * Energy Certificate Simulator - U-Value Defaults
 *
 * Default thermal transmittance values [W/m²K] based on:
 * - Building construction period
 * - Insulation level
 * - Polish building regulations (WT 2021, historical requirements)
 */

import type { BuildingPeriod, InsulationLevel, WindowType } from '../types';

// =============================================================================
// EXTERNAL WALL U-VALUES [W/m²K]
// =============================================================================

/**
 * Wall U-values by building period and insulation level
 * Based on typical Polish construction methods
 */
export const WALL_U_VALUES: Record<BuildingPeriod, Record<InsulationLevel, number>> = {
  before_1945: { none: 1.5, partial: 0.8, full: 0.4, modern: 0.2 },
  '1945_1966': { none: 1.2, partial: 0.7, full: 0.35, modern: 0.2 },
  '1967_1985': { none: 1.0, partial: 0.6, full: 0.3, modern: 0.18 },
  '1986_1998': { none: 0.7, partial: 0.5, full: 0.28, modern: 0.18 },
  '1999_2008': { none: 0.5, partial: 0.4, full: 0.25, modern: 0.15 },
  '2009_2013': { none: 0.35, partial: 0.3, full: 0.23, modern: 0.15 },
  '2014_2020': { none: 0.25, partial: 0.23, full: 0.2, modern: 0.12 },
  '2021_plus': { none: 0.2, partial: 0.18, full: 0.15, modern: 0.1 },
};

/**
 * Get wall U-value for given building period and insulation level
 */
export function getWallUValue(
  buildingPeriod: BuildingPeriod,
  insulationLevel: InsulationLevel
): number {
  return WALL_U_VALUES[buildingPeriod]?.[insulationLevel] ?? 0.5;
}

// =============================================================================
// ROOF U-VALUES [W/m²K]
// =============================================================================

/**
 * Simplified roof periods (grouped for simplicity)
 */
type RoofPeriod = 'before_1985' | '1986_2008' | '2009_plus';

/**
 * Map building period to roof period
 */
function mapToRoofPeriod(buildingPeriod: BuildingPeriod): RoofPeriod {
  switch (buildingPeriod) {
    case 'before_1945':
    case '1945_1966':
    case '1967_1985':
      return 'before_1985';
    case '1986_1998':
    case '1999_2008':
      return '1986_2008';
    default:
      return '2009_plus';
  }
}

/**
 * Roof U-values by period and insulation level
 */
export const ROOF_U_VALUES: Record<RoofPeriod, Record<InsulationLevel, number>> = {
  before_1985: { none: 1.0, partial: 0.5, full: 0.25, modern: 0.15 },
  '1986_2008': { none: 0.5, partial: 0.35, full: 0.2, modern: 0.12 },
  '2009_plus': { none: 0.25, partial: 0.2, full: 0.15, modern: 0.1 },
};

/**
 * Get roof U-value for given building period and insulation level
 */
export function getRoofUValue(
  buildingPeriod: BuildingPeriod,
  insulationLevel: InsulationLevel
): number {
  const roofPeriod = mapToRoofPeriod(buildingPeriod);
  return ROOF_U_VALUES[roofPeriod]?.[insulationLevel] ?? 0.3;
}

// =============================================================================
// WINDOW U-VALUES [W/m²K]
// =============================================================================

/**
 * Window U-values by glazing type
 */
export const WINDOW_U_VALUES: Record<WindowType, number> = {
  single: 5.0,
  double_old: 2.8,
  double_new: 1.3,
  triple: 0.8,
};

/**
 * Get window U-value for given window type
 */
export function getWindowUValue(windowType: WindowType): number {
  return WINDOW_U_VALUES[windowType] ?? 1.3;
}

// =============================================================================
// FLOOR U-VALUES [W/m²K]
// =============================================================================

/**
 * Simplified floor periods
 */
type FloorPeriod = 'before_1998' | '1999_2013' | '2014_2020' | '2021_plus';

/**
 * Map building period to floor period
 */
function mapToFloorPeriod(buildingPeriod: BuildingPeriod): FloorPeriod {
  switch (buildingPeriod) {
    case 'before_1945':
    case '1945_1966':
    case '1967_1985':
    case '1986_1998':
      return 'before_1998';
    case '1999_2008':
    case '2009_2013':
      return '1999_2013';
    case '2014_2020':
      return '2014_2020';
    default:
      return '2021_plus';
  }
}

/**
 * Floor U-values by period
 */
export const FLOOR_U_VALUES: Record<FloorPeriod, number> = {
  before_1998: 1.0,
  '1999_2013': 0.6,
  '2014_2020': 0.4,
  '2021_plus': 0.3,
};

/**
 * Get floor U-value for given building period
 */
export function getFloorUValue(buildingPeriod: BuildingPeriod): number {
  const floorPeriod = mapToFloorPeriod(buildingPeriod);
  return FLOOR_U_VALUES[floorPeriod] ?? 0.5;
}

// =============================================================================
// THERMAL BRIDGES [W/mK]
// =============================================================================

/**
 * Linear thermal bridge coefficient by building quality
 */
export const THERMAL_BRIDGE_COEFFICIENTS = {
  /** Buildings with significant thermal bridges */
  high: 0.15,
  /** Standard buildings */
  medium: 0.1,
  /** Well-detailed buildings (modern) */
  low: 0.05,
  /** Buildings with thermal bridge-free design */
  minimal: 0.02,
} as const;

/**
 * Get thermal bridge coefficient based on building period
 */
export function getThermalBridgeCoefficient(buildingPeriod: BuildingPeriod): number {
  switch (buildingPeriod) {
    case 'before_1945':
    case '1945_1966':
      return THERMAL_BRIDGE_COEFFICIENTS.high;
    case '1967_1985':
    case '1986_1998':
      return THERMAL_BRIDGE_COEFFICIENTS.medium;
    case '1999_2008':
    case '2009_2013':
      return THERMAL_BRIDGE_COEFFICIENTS.low;
    default:
      return THERMAL_BRIDGE_COEFFICIENTS.minimal;
  }
}

// =============================================================================
// WT 2021 LIMITS [W/m²K]
// =============================================================================

/**
 * Maximum U-values according to WT 2021 (current Polish building code)
 */
export const WT_2021_LIMITS = {
  walls: 0.2,
  roof: 0.15,
  floor: 0.3,
  windows: 0.9,
  doors: 1.3,
} as const;

/**
 * Check if U-value meets WT 2021 requirements
 */
export function meetsWT2021(
  component: keyof typeof WT_2021_LIMITS,
  uValue: number
): boolean {
  return uValue <= WT_2021_LIMITS[component];
}
